import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <>
        <style>{`
          @keyframes bouncy {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-20px); }
          }
        `}</style>
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-white dark:bg-gray-950">
          <div className="flex items-end gap-2 h-10">
            <div className="w-4 h-4 rounded-full bg-[#3967fe] animate-[bouncy_0.5s_ease-in-out_infinite] [animation-delay:-0.3s]" />
            <div className="w-4 h-4 rounded-full bg-[#3967fe] animate-[bouncy_0.5s_ease-in-out_infinite] [animation-delay:-0.15s]" />
            <div className="w-4 h-4 rounded-full bg-[#3967fe] animate-[bouncy_0.5s_ease-in-out_infinite]" />
          </div>
        </div>
      </>
    )
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const isOwner = user?.access?.isOwner || false;
  const needsOnboarding = isOwner && user.user?.isOnboardingCompleted === false;

  if (needsOnboarding) {
    if (location.pathname !== '/onboarding') {
      return <Navigate to="/onboarding" state={{ from: location }} replace />;
    }
    return <>{children}</>;
  }

  if (location.pathname === '/onboarding') {
    return <Navigate to="/" replace />;
  }

  // Check subscription status for owners (only after onboarding is completed)
  const isExpired = user?.subscription?.status === 'expired';
  const isTrialExpired = !!(user?.subscription?.isTrial && user?.subscription?.endDate && new Date(user.subscription.endDate) < new Date());
  const hasNoSubscription = !user?.subscription?.id;
  const isUnsubscribedOrExpired = isExpired || isTrialExpired || hasNoSubscription;
  const isPlanPageRoute = ['/upgrade-plan', '/plans', '/payment-history'].includes(location.pathname);

  if (isOwner && isUnsubscribedOrExpired && !isPlanPageRoute) {
    return <Navigate to="/upgrade-plan#monthly" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}